import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Typography,
  Grid,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import HubIcon from '@mui/icons-material/Hub';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import StatusChip from './shared/StatusChip';
import ThemeSelector from './ThemeSelector';

// ── Helpers ────────────────────────────────────────────────────────
const getInitials = (user: { full_name?: string; username?: string; email?: string } | null): string => {
  if (!user) return '?';
  const name = user.full_name || user.username || user.email || '';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const DEMO_FRONTEND_URL =
  process.env.REACT_APP_DEMO_FRONTEND_URL || 'http://localhost:3000';

// ── Component ──────────────────────────────────────────────────────
const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleOpenDashboard = () => {
    handleMenuClose();
    window.open(DEMO_FRONTEND_URL, '_blank');
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const menuSections = [
    {
      label: 'IAM',
      items: [
        { label: 'Organisations', path: '/organisations', icon: <BusinessIcon fontSize="small" /> },
      ],
    },
    {
      label: 'Model',
      items: [
        { label: 'Guidelines', path: '/guidelines', icon: <DescriptionIcon fontSize="small" /> },
      ],
    },
    {
      label: 'System',
      items: [
        { label: 'System Health', path: '/system-health', icon: <MonitorHeartIcon fontSize="small" /> },
        { label: 'Celery Monitor', path: '/celery', icon: <HubIcon fontSize="small" /> },
      ],
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* AppBar — transparent, matching demo-frontend TopToolbar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <Grid container alignItems="center" width="100%">
            {/* Logo */}
            <Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/logo-notext.png"
                alt="Logo"
                sx={{ height: 30, '&:hover': { cursor: 'pointer' } }}
                onClick={() => navigate('/')}
              />
            </Grid>

            <Grid size={{ xs: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
              <ThemeSelector />

              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{ p: 0, border: `1px solid ${theme.palette.divider}` }}
              >
                <Avatar
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100',
                    color: 'text.primary',
                    width: 36,
                    height: 36,
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {getInitials(user)}
                </Avatar>
              </IconButton>

              {/* Dropdown Menu */}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: '220px' } }}
              >
                {/* User info */}
                <MenuItem disabled>
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" alignItems="flex-start" gap={0.5}>
                      <Typography variant="body2" fontWeight={700}>
                        {user?.full_name || user?.username || 'Admin'}
                      </Typography>
                      <StatusChip status="Super Admin" size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email || ''}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                
                {/* Dashboard (kept separate at top) */}
                <MenuItem
                  onClick={() => handleNavigate('/')}
                  selected={location.pathname === '/'}
                >
                  <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                  <Typography variant="body2">Dashboard</Typography>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />

                {/* Grouped menu sections */}
                {menuSections.map((section, sectionIndex) => (
                  <React.Fragment key={section.label}>
                    <MenuItem disabled sx={{ paddingTop: 0, paddingBottom: 0 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {section.label}
                      </Typography>
                    </MenuItem>
                    {section.items.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => handleNavigate(item.path)}
                        selected={location.pathname === item.path}
                        sx={{ pl: 3 }}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <Typography variant="body2">{item.label}</Typography>
                      </MenuItem>
                    ))}
                    {sectionIndex < menuSections.length - 1 && <Divider sx={{ my: 0.5 }} />}
                  </React.Fragment>
                ))}
                <Divider sx={{ my: 0.5 }} />

                {/* Demo Dashboard link */}
                <MenuItem onClick={handleOpenDashboard}>
                  <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                  <Typography variant="body2" sx={{ flex: 1 }}>Demo Dashboard</Typography>
                  <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />

                {/* Logout */}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <Typography variant="body2">Log out</Typography>
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 5, pt: 3 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
